import { RydeUiUnit } from "./common.models";

export enum AIRoleTypeEnum {
  User = "user",
  Assistant = "assistant",
}

export enum AiStreamEventEnum {
  Session = "session",
  Chunk = "chunk",
  Elements = "elements",
  Done = "done",
  Error = "error",
}

export type AIChatRoleType = `${AIRoleTypeEnum}`;

/* ===================== AI UI elements ===================== */

/**
 * Structured UI controls the assistant can attach to a turn.
 *
 * Mirrors `IAiUiElement` in the backend (`libs/services/src/aiService/utils/ai.models.ts`).
 * Two invariants the renderer relies on:
 *
 * * **`text` is a complete answer on its own.** Elements are additive, so failing to render
 *   one loses no information — never block a message on them.
 * * **Unknown types are skipped, not errors.** The runtime ships new element types faster
 *   than clients do; `schemaVersion` changes only when an existing shape changes.
 *
 * The assistant never supplies a URL or a phone number. `navigate` and `entityRef` name an
 * entity and an id, and the client resolves the route itself (see `entityPathMappings`);
 * `handoff` carries a value the backend filled from company settings.
 */
export interface IAiChoiceOption {
  /** Button text. Plain text — never rendered as markdown. */
  label: string;
  /** Sent as the user's next message when tapped, exactly as if they had typed it. */
  value: string;
}

export type TAiHandoffChannel = "whatsapp" | "phone" | "email";

export type TAiUiElement =
  | { type: "choice"; id: string; multi: boolean; options: IAiChoiceOption[] }
  | { type: "navigate"; label: string; target: { entity: string; id?: number } }
  | {
      type: "handoff";
      channel: TAiHandoffChannel;
      label: string;
      value: string;
    }
  | { type: "entityRef"; entity: string; id: number; label: string };

export interface IChatHistoryItem {
  role: AIChatRoleType;
  text: string;
  reasoning?: string;
  messageIndex?: number;
  /** Controls attached to this turn. Only the latest assistant message stays interactive. */
  elements?: TAiUiElement[];
}

export interface IAIQuestionRequestPayload {
  sessionId?: string;
  question: string;
  entity: RydeUiUnit;
  ids?: number[];
}

export interface IAIQuestionRequest {
  payload: IAIQuestionRequestPayload;
}

export type IAIQuestionResponse = {
  text: string;
  sessionId: string;
  reasoning?: string;
};

export type IAIQuestionDetailsResponse = {
  reasoning: string;
};

export type TAiStreamEvent =
  | { type: AiStreamEventEnum.Session; sessionId: string }
  | { type: AiStreamEventEnum.Chunk; text: string }
  /** Arrives once, after the text has streamed and before `done`. */
  | {
      type: AiStreamEventEnum.Elements;
      schemaVersion: number;
      elements: TAiUiElement[];
    }
  | { type: AiStreamEventEnum.Done; reasoning?: string }
  | { type: AiStreamEventEnum.Error; message: string };

export interface IAiStreamCallbacks {
  onChunk: (text: string) => void;
  onSession: (sessionId: string) => void;
  onDone: (reasoning?: string) => void;
  onError: (message: string) => void;
  /** Optional so existing callers keep compiling; the stream reader skips it when absent. */
  onElements?: (elements: TAiUiElement[]) => void;
}
